import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useCallback, useContext, useMemo } from 'react';
import { toast } from 'sonner';

import { useAuth } from '@/contexts/AuthContext';
import {
    deletePin,
    fetchPinnedPages,
    insertPin,
    type PinnedPage,
    renamePin,
    reorderPins,
} from '@/services/pinnedPages';

interface PinnedPagesContextValue {
    pins: PinnedPage[];
    loading: boolean;
    isPinned: (path: string) => boolean;
    pin: (path: string, label: string) => void;
    unpin: (path: string) => void;
    rename: (path: string, label: string) => void;
    reorder: (orderedPaths: string[]) => void;
}

const PinnedPagesContext = createContext<PinnedPagesContextValue | null>(null);

const pinnedPagesKey = (userId: string | undefined) => ['pinned-pages', userId] as const;

export function PinnedPagesProvider({ children }: { children: ReactNode }) {
    const { user } = useAuth();
    const userId = user?.id;
    const queryClient = useQueryClient();

    const queryKey = useMemo(() => pinnedPagesKey(userId), [userId]);

    const { data: pins = [], isLoading } = useQuery({
        queryKey,
        queryFn: () => fetchPinnedPages(userId as string),
        enabled: !!userId,
        staleTime: 5 * 60 * 1000,
    });

    const invalidate = useCallback(
        () => queryClient.invalidateQueries({ queryKey }),
        [queryClient, queryKey]
    );

    const pinMutation = useMutation({
        mutationFn: ({ path, label }: { path: string; label: string }) =>
            insertPin(userId as string, path, label, pins.length),
        onSuccess: invalidate,
        onError: () => {
            toast.error('Could not pin this page.');
        },
    });

    const unpinMutation = useMutation({
        mutationFn: (path: string) => deletePin(userId as string, path),
        onSuccess: invalidate,
        onError: () => {
            toast.error('Could not unpin this page.');
        },
    });

    const renameMutation = useMutation({
        mutationFn: ({ path, label }: { path: string; label: string }) =>
            renamePin(userId as string, path, label),
        onSuccess: invalidate,
        onError: () => {
            toast.error('Could not rename this pinned page.');
        },
    });

    const reorderMutation = useMutation({
        mutationFn: (orderedPaths: string[]) => {
            const byPath = new Map(pins.map((p) => [p.path, p]));
            const orderedPins = orderedPaths
                .map((path) => byPath.get(path))
                .filter((pin): pin is PinnedPage => pin !== undefined);
            return reorderPins(userId as string, orderedPins);
        },
        // Optimistic so the drag doesn't snap back while the writes land.
        onMutate: (orderedPaths: string[]) => {
            const byPath = new Map(pins.map((p) => [p.path, p]));
            const next = orderedPaths
                .map((path, index) => {
                    const row = byPath.get(path);
                    return row ? { ...row, position: index } : null;
                })
                .filter((p): p is PinnedPage => p !== null);
            queryClient.setQueryData(queryKey, next);
        },
        onError: () => {
            toast.error('Could not reorder pinned pages.');
        },
        onSettled: invalidate,
    });

    const value = useMemo<PinnedPagesContextValue>(
        () => ({
            pins,
            loading: isLoading,
            isPinned: (path: string) => pins.some((p) => p.path === path),
            pin: (path: string, label: string) => {
                if (userId) pinMutation.mutate({ path, label });
            },
            unpin: (path: string) => {
                if (userId) unpinMutation.mutate(path);
            },
            rename: (path: string, label: string) => {
                if (userId) renameMutation.mutate({ path, label });
            },
            reorder: (orderedPaths: string[]) => {
                if (userId) reorderMutation.mutate(orderedPaths);
            },
        }),
        [pins, isLoading, userId, pinMutation, unpinMutation, renameMutation, reorderMutation]
    );

    return <PinnedPagesContext.Provider value={value}>{children}</PinnedPagesContext.Provider>;
}

export function usePinnedPages(): PinnedPagesContextValue {
    const context = useContext(PinnedPagesContext);
    if (!context) {
        throw new Error('usePinnedPages must be used within a PinnedPagesProvider');
    }
    return context;
}
